import { useMemo } from "react"

import { UTCDateMini } from "@date-fns/utc"

import { type ForecastBreakdownRow } from "~/server/api/utils/sharedSqlQueries"
import { formatWeekRangeLabel } from "~/utils/calendarUtils"
import { DateFormatType } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"
import { extractColor, tooltipSwatch } from "~/utils/graphUtils"
import { useFiscalWeek } from "~/hooks/useFiscalWeek"
import { formatName } from "~/utils/namingUtils"
import { getTailwindValue } from "~/utils/tailwind"
import SuperChart, { type DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import { ChartType, DateType, TodayPlotLine } from "~/constants/graphs"

type Props = {
  data: ForecastBreakdownRow[]
  lastYearPreOrders: number
}

export default function PreorderFactorsChart({ data, lastYearPreOrders }: Props) {
  const { formatWeek } = useFiscalWeek()

  const sorted = useMemo(
    () =>
      [...data]
        .filter((d) => d.preorderUnknownRatio != null || d.lastYearPreOrders != null)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [data],
  )

  const series = useMemo((): DateChartSeries[] => {
    const toMs = (d: ForecastBreakdownRow) => new Date(d.date).getTime()

    const knownPreorders: DateChartSeries = {
      name: `Known ${formatName("preorder", true)}`,
      data: sorted.map((d) => ({
        milliseconds: toMs(d),
        value: d.knownPreorders != null ? Number(d.knownPreorders) : 0,
      })),
      type: "column",
      colors: [getTailwindValue("--color-purple-500") || "#a855f7"],
      stack: "preorders",
    }

    const expectedPreorders: DateChartSeries = {
      name: `Forecasted ${formatName("preorder", true)}`,
      data: sorted.map((d) => ({
        milliseconds: toMs(d),
        value: d.unknownPreOrder != null ? Math.round(Number(d.unknownPreOrder)) : null,
      })),
      type: "column",
      colors: [getTailwindValue("--color-green-500") || "#22c55e"],
      stack: "preorders",
    }

    const lastYearPreordersSeries: DateChartSeries = {
      name: `Last year ${formatName("preorder", true)}`,
      data: sorted.map((d) => ({
        milliseconds: toMs(d),
        value: d.lastYearPreOrders != null ? Math.round(Number(d.lastYearPreOrders)) : null,
      })),
      type: "spline",
      colors: [getTailwindValue("--color-orange-500") || "#f97316"],
      dashStyle: "Dash",
      hidden: true,
    }

    const knownRatio: DateChartSeries = {
      name: "Known ratio",
      data: sorted
        .filter((d) => d.preorderUnknownRatio != null)
        .map((d) => ({
          milliseconds: toMs(d),
          value: (1 - (d.preorderUnknownRatio ?? 0)) * 100,
        })),
      type: "spline",
      colors: [getTailwindValue("--color-blue-500") || "#3b82f6"],
      yAxis: 1,
      hidden: true,
    }

    return [knownPreorders, expectedPreorders, lastYearPreordersSeries, knownRatio]
  }, [sorted])

  const next52wTotal = useMemo(() => {
    return data.reduce((sum, d) => {
      const known = d.knownPreorders != null ? Number(d.knownPreorders) : 0
      const unknown = d.unknownPreOrder != null ? Number(d.unknownPreOrder) : 0
      return sum + known + unknown
    }, 0)
  }, [data])

  const tooltipFormatter = useMemo(() => {
    return function (this: Highcharts.Point) {
      const header = formatWeek(new UTCDateMini(this.category), DateFormatType.GraphWeek)
      const weekRange = formatWeekRangeLabel(new UTCDateMini(this.category))
      let html = `<div style="padding:8px 12px;"><span style="font-weight:500;">${header}</span><span style="font-weight:400;color:var(--color-fg-muted);"> · ${weekRange}</span></div>`
      html += `<div style="border-top:1px;border-color:var(--color-border-base);border-style:dotted;width:100%;"></div>`
      html += `<div style='padding:8px 12px;display:flex;flex-direction:column;gap:4px;width:100%;'>`
      for (const point of this.points ?? []) {
        const isPercent = point.series.yAxis.options.opposite === true
        const value = isPercent
          ? `${formatNumber(point.y ?? 0, 1)}%`
          : formatNumber(point.y ?? 0, 1)
        const color = extractColor(point.color)
        html += `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;"><div style="display:flex;align-items:center;">${tooltipSwatch(color)}<div style="margin-left:8px;margin-right:4px;font-weight:400;">${point.series.name}</div></div><span style="text-align:right;font-weight:500;color:var(--color-fg-base);">${value}</span></div>`
      }
      html += `</div>`
      return `<div style="z-index:9999;box-shadow:0px 0px 0px 1px rgba(0,0,0,0.08),0px 4px 8px 0px rgba(0,0,0,0.08),0px 8px 16px 0px rgba(0,0,0,0.08);border-radius:12px;border:1px solid var(--color-border-base);background:var(--color-background-background);overflow:hidden;">${html}</div>`
    }
  }, [formatWeek])

  if (sorted.length === 0) return null

  const dateRange = {
    start: new Date(sorted[0]!.date),
    end: new Date(sorted[sorted.length - 1]!.date),
  }

  return (
    <div>
      <div className="label-base text-fg-base mb-0.5">
        {formatName("preorder", true, true)} breakdown
      </div>
      <div className="label-sm text-fg-subtle mb-1">
        Last 52w:{" "}
        <span className="text-fg-base">{formatNumber(Math.round(lastYearPreOrders))}</span>
        {" · "}Next 52w:{" "}
        <span className="text-fg-base">{formatNumber(Math.round(next52wTotal))}</span>
        {" · "}Diff:{" "}
        <span className={next52wTotal >= lastYearPreOrders ? "text-green-600" : "text-red-500"}>
          {next52wTotal >= lastYearPreOrders ? "+" : ""}
          {formatNumber(Math.round(next52wTotal - lastYearPreOrders))}
        </span>
      </div>
      <SuperChart
        type={ChartType.TimeSeries}
        data={series}
        dateRange={dateRange}
        dateType={DateType.Weekly}
        height={340}
        isStacked
        disableAnimation
        todayPlotLine={TodayPlotLine.Hide}
        customTooltipFormatter={tooltipFormatter}
        yAxis={[
          { title: { text: undefined }, min: 0 },
          {
            title: { text: undefined },
            min: 0,
            max: 100,
            opposite: true,
            labels: { format: "{value}%", style: { color: "#d1d5db", fontSize: "10px" } },
            gridLineWidth: 0,
          },
        ]}
      />
    </div>
  )
}
